import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import axiosInstance from '../../utils/axiosConfig';
import { OneriKalemi } from './OrtakNotlari';

// v1.26.0 — "Daire Öner": kişinin kalan hakkına göre bölüm paketleri (AI destekli ya da kural tabanlı).
// Paket seçilir ya da listeden bölüm / oran elle değiştirilir; deneme hesabı anında güncellenir; Onayla ile
// paylaşımlar oluşturulur.

interface Aday {
    bolumID: number; ad: string; blok: string | null; kat: string | null; tur: string | null; tip: string | null; cephe: string | null;
    m2: number; fiyat: number; kalanOran: number; kalanTutar: number;
}
interface Paket { baslik: string; gerekce: string; kaynak: 'ai' | 'kural'; toplam: number; kalemler: OneriKalemi[]; }
interface OneriSonucu {
    kaynak: 'ai' | 'kural'; aiAktif: boolean; genelYorum: string | null; mesaj: string | null;
    hakEttigi: number; aldigi: number; kalan: number; paketler: Paket[]; adaylar: Aday[];
}

const tl = (n: number) => `${n.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ₺`;
const yuzde = (n: number) => n.toLocaleString('tr-TR', { maximumFractionDigits: 4 });
const sayi = (s: string) => { const v = parseFloat(s.replace(',', '.')); return isNaN(v) ? 0 : v; };

const DaireOneriPaneli: React.FC<{
    arsaId: number;
    kisi: { tip: 'ortak' | 'muteahhit'; id: number; adSoyad: string };
    /** Talep çözüm önerisinden gelen paket — panel açılınca seçili gelir */
    baslangicKalemler?: OneriKalemi[] | null;
    onOnizle: (bolumID: number) => void;
    /** Paylaşımlar oluşturulduktan sonra (sayfa verisini yenilemek için) */
    onTamam: () => Promise<void> | void;
    onKapat: () => void;
}> = ({ arsaId, kisi, baslangicKalemler, onOnizle, onTamam, onKapat }) => {
    const [veri, setVeri] = useState<OneriSonucu | null>(null);
    const [yukleniyor, setYukleniyor] = useState(false);
    const [secim, setSecim] = useState<Record<number, string>>({});
    const [seciliPaket, setSeciliPaket] = useState<number | null>(null);
    const [tumu, setTumu] = useState(false);
    const [uygulaniyor, setUygulaniyor] = useState(false);

    const paketSec = (kalemler: OneriKalemi[], idx: number | null) => {
        setSecim(Object.fromEntries(kalemler.map(k => [k.bolumID, String(k.oran)])));
        setSeciliPaket(idx);
    };

    const yukle = useCallback(async (ilk = false) => {
        setYukleniyor(true);
        try {
            const r = await axiosInstance.post(`/api/KatKarsiligArsa/${arsaId}/daire-oneri`, { tip: kisi.tip, id: kisi.id });
            const v: OneriSonucu = r.data;
            setVeri(v);
            if (ilk && baslangicKalemler?.length) paketSec(baslangicKalemler, null);
            else if (v.paketler.length > 0) paketSec(v.paketler[0].kalemler, 0);
            else { setSecim({}); setSeciliPaket(null); }
        } catch { toast.error('Öneriler alınamadı.'); }
        finally { setYukleniyor(false); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [arsaId, kisi.tip, kisi.id]);
    useEffect(() => { yukle(true); }, [yukle]);

    // Talep çözümünden yeni bir paket gelirse seç
    useEffect(() => { if (baslangicKalemler?.length && veri) paketSec(baslangicKalemler, null); }, [baslangicKalemler]); // eslint-disable-line react-hooks/exhaustive-deps

    const adayHarita = useMemo(() => new Map((veri?.adaylar ?? []).map(a => [a.bolumID, a])), [veri]);
    const onerilenIdler = useMemo(() => new Set((veri?.paketler ?? []).flatMap(p => p.kalemler.map(k => k.bolumID))), [veri]);
    const satirlar = useMemo(() => {
        const l = veri?.adaylar ?? [];
        const secili = (a: Aday) => secim[a.bolumID] !== undefined;
        return (tumu ? l : l.filter(a => secili(a) || onerilenIdler.has(a.bolumID)))
            .slice().sort((a, b) => Number(secili(b)) - Number(secili(a)));
    }, [veri, tumu, secim, onerilenIdler]);

    if (!veri) {
        return (
            <div className="kka-oneri-kutu">
                <div className="kka-empty"><i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }}></i>Uygun bölümler hesaplanıyor{yukleniyor ? '…' : ''}</div>
            </div>
        );
    }

    const kalemler = Object.entries(secim).map(([id, o]) => {
        const a = adayHarita.get(Number(id));
        const oran = sayi(o);
        return { a, oran, tutar: a ? a.fiyat * oran / 100 : 0, gecersiz: !a || oran <= 0 || oran > (a?.kalanOran ?? 0) + 0.0001 };
    });
    const toplam = kalemler.reduce((s, k) => s + k.tutar, 0);
    const kalanSonra = veri.kalan - toplam;
    const tamamlanma = veri.hakEttigi > 0 ? ((veri.aldigi + toplam) / veri.hakEttigi) * 100 : 0;
    const gecersizVar = kalemler.some(k => k.gecersiz);
    const asim = kalanSonra < -Math.max(1, veri.hakEttigi * 0.005);

    const toggle = (a: Aday) => {
        setSeciliPaket(null);
        setSecim(s => {
            const y = { ...s };
            if (y[a.bolumID] !== undefined) { delete y[a.bolumID]; return y; }
            // Yeni seçilen bölüm: kalan hakkı dolduracak kadar (bölümün kalan payını aşmadan)
            const mevcut = Object.entries(y).reduce((t, [id, o]) => t + (adayHarita.get(Number(id))?.fiyat ?? 0) * sayi(o) / 100, 0);
            const gereken = Math.max(0, veri.kalan - mevcut);
            const oran = gereken > 0 ? Math.min(a.kalanOran, Math.floor((gereken / a.fiyat) * 100 * 10000) / 10000) : a.kalanOran;
            y[a.bolumID] = String(oran > 0 ? oran : a.kalanOran);
            return y;
        });
    };

    const onayla = async () => {
        if (kalemler.length === 0 || gecersizVar) return;
        const ozet = kalemler.map(k => `• ${k.a!.ad}: %${yuzde(k.oran)} (${tl(k.tutar)})`).join('\n');
        if (!window.confirm(`${kisi.adSoyad} için ${kalemler.length} paylaşım oluşturulacak:\n\n${ozet}\n\nToplam ${tl(toplam)} — kalan hak ${tl(kalanSonra)}${asim ? '\n\nUYARI: hak edilen pay aşılıyor!' : ''}\n\nOnaylıyor musunuz?`)) return;
        setUygulaniyor(true);
        let basarili = 0;
        const kaynak = seciliPaket != null ? veri.paketler[seciliPaket]?.kaynak : null;
        for (const k of kalemler) {
            try {
                await axiosInstance.post(`/api/KatKarsiligArsa/${arsaId}/bolumler/${k.a!.bolumID}/paylasimlar`, {
                    ortakID: kisi.tip === 'ortak' ? kisi.id : null,
                    muteahhitID: kisi.tip === 'muteahhit' ? kisi.id : null,
                    oranYuzde: Number(k.oran.toFixed(4)),
                    aciklamaNot: kaynak === 'ai' ? 'Daire önerisi (AI)' : 'Daire önerisi',
                });
                basarili++;
            } catch (e: any) {
                toast.error(`${k.a!.ad}: ${typeof e?.response?.data === 'string' ? e.response.data : 'paylaşım eklenemedi'}`);
            }
        }
        if (basarili > 0) toast.success(`${basarili} paylaşım oluşturuldu.`);
        await onTamam();
        await yukle();
        setUygulaniyor(false);
    };

    return (
        <div className="kka-oneri-kutu">
            <div className="kka-oneri-baslik">
                <i className="fas fa-magic" style={{ color: '#7c3aed' }}></i>
                Daire Önerisi — {kisi.adSoyad}
                <span className={`kka-kaynak${veri.kaynak === 'kural' ? ' kural' : ''}`} title={veri.kaynak === 'ai' ? 'Talepler ve notlar dikkate alınarak yapay zekâ ile seçildi; tutarlar sistemce doğrulandı' : veri.aiAktif ? 'AI yanıt vermedi; kural tabanlı öneri' : 'Yapay zekâ ayarı kapalı; kural tabanlı öneri'}>
                    <i className={`fas ${veri.kaynak === 'ai' ? 'fa-magic' : 'fa-calculator'}`}></i>{veri.kaynak === 'ai' ? 'AI destekli' : 'Kural tabanlı'}
                </span>
                <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                    <button className="kka-mini-btn" onClick={() => yukle()} disabled={yukleniyor} title="Önerileri yenile">
                        <i className={`fas fa-sync-alt${yukleniyor ? ' fa-spin' : ''}`}></i>Yenile
                    </button>
                    <button className="kka-mini-btn" onClick={onKapat} title="Kapat"><i className="fas fa-times"></i></button>
                </span>
            </div>

            {veri.mesaj && <div style={{ background: '#f1f5f9', borderRadius: 7, padding: '8px 12px', fontSize: '0.85rem', color: '#475569', marginBottom: 10 }}>{veri.mesaj}</div>}
            {veri.genelYorum && <div className="kka-cozum" style={{ marginTop: 0, marginBottom: 10 }}>{veri.genelYorum}</div>}

            {veri.paketler.length > 0 && (
                <div className="kka-paketler">
                    {veri.paketler.map((p, i) => (
                        <button key={i} className={`kka-paket${seciliPaket === i ? ' secili' : ''}`} onClick={() => paketSec(p.kalemler, i)}>
                            <div className="kka-paket-ad">
                                {p.kaynak === 'ai' && <i className="fas fa-magic" style={{ color: '#7c3aed' }} title="AI önerisi"></i>}
                                {p.baslik}
                            </div>
                            <div className="kka-paket-toplam">{tl(p.toplam)} · kalan {tl(veri.kalan - p.toplam)}</div>
                            <div className="kka-paket-gerekce">{p.kalemler.map(k => `${k.bolumAdi}${k.oran < (adayHarita.get(k.bolumID)?.kalanOran ?? 100) - 0.0001 ? ` %${yuzde(k.oran)}` : ''}`).join(' + ')}</div>
                            {p.gerekce && <div className="kka-paket-gerekce" style={{ marginTop: 3 }}>{p.gerekce}</div>}
                        </button>
                    ))}
                </div>
            )}

            {veri.adaylar.length > 0 && (
                <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#475569' }}>
                            {tumu ? `Paylaşılmamış payı olan tüm bölümler (${veri.adaylar.length})` : 'Önerilen bölümler'} — seçimi ve oranı değiştirerek deneyin
                        </span>
                        <label style={{ marginLeft: 'auto', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
                            <input type="checkbox" checked={tumu} onChange={e => setTumu(e.target.checked)} /> Tüm uygun bölümleri göster
                        </label>
                    </div>
                    <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff' }}>
                        <table className="kka-sub-table" style={{ fontSize: '0.82rem', margin: 0 }}>
                            <thead>
                                <tr><th style={{ width: 30 }}></th><th>Bölüm</th><th>Blok / Kat</th><th>Tür</th><th>m²</th><th>Fiyat</th><th>Boş pay</th><th style={{ width: 110 }}>Oran (%)</th><th>Tutar</th></tr>
                            </thead>
                            <tbody>
                                {satirlar.map(a => {
                                    const o = secim[a.bolumID];
                                    const secili = o !== undefined;
                                    const oran = secili ? sayi(o) : 0;
                                    const hatali = secili && (oran <= 0 || oran > a.kalanOran + 0.0001);
                                    return (
                                        <tr key={a.bolumID} style={secili ? { background: '#fff7ed' } : undefined}>
                                            <td><input type="checkbox" checked={secili} onChange={() => toggle(a)} /></td>
                                            <td>
                                                <button className="kka-bolum-link" onClick={() => onOnizle(a.bolumID)} title="Bölüm bilgisi, kat planı ve görünümler">{a.ad}</button>
                                                {onerilenIdler.has(a.bolumID) && <i className="fas fa-star" style={{ color: '#f59e0b', marginLeft: 5, fontSize: 10 }} title="Önerilen paketlerde"></i>}
                                            </td>
                                            <td>{[a.blok, a.kat].filter(Boolean).join(' / ') || '–'}</td>
                                            <td>{a.tur || a.tip || '–'}{a.cephe ? <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{a.cephe}</div> : null}</td>
                                            <td>{a.m2 ? a.m2.toLocaleString('tr-TR') : '–'}</td>
                                            <td>{tl(a.fiyat)}</td>
                                            <td>%{yuzde(a.kalanOran)}</td>
                                            <td>
                                                {secili ? (
                                                    <input type="number" value={o} min={0} max={a.kalanOran} step="0.01"
                                                        onChange={e => { setSeciliPaket(null); setSecim(s => ({ ...s, [a.bolumID]: e.target.value })); }}
                                                        style={{ width: 90, padding: '3px 6px', border: `1px solid ${hatali ? '#e74c3c' : '#cd6906'}`, borderRadius: 5 }} />
                                                ) : <span style={{ color: '#cbd5e1' }}>–</span>}
                                            </td>
                                            <td style={{ fontWeight: 700 }}>{secili ? tl(a.fiyat * oran / 100) : '–'}</td>
                                        </tr>
                                    );
                                })}
                                {satirlar.length === 0 && <tr><td colSpan={9} style={{ textAlign: 'center', color: '#94a3b8' }}>Önerilen bölüm yok — "Tüm uygun bölümleri göster" ile seçin.</td></tr>}
                            </tbody>
                        </table>
                    </div>

                    {/* Deneme hesabı */}
                    <div className="kka-deneme">
                        <div><div className="e">Hak ettiği</div><div className="d">{tl(veri.hakEttigi)}</div></div>
                        <div><div className="e">Şu an aldığı</div><div className="d">{tl(veri.aldigi)}</div></div>
                        <div><div className="e">Seçilen ({kalemler.length})</div><div className="d" style={{ color: '#fdba74' }}>{tl(toplam)}</div></div>
                        <div><div className="e">Sonra kalan</div><div className="d" style={{ color: asim ? '#fca5a5' : '#86efac' }}>{tl(kalanSonra)}</div></div>
                        <div><div className="e">Tamamlanma</div><div className="d">%{tamamlanma.toLocaleString('tr-TR', { maximumFractionDigits: 1 })}</div></div>
                        <div style={{ textAlign: 'right' }}>
                            <button className="kka-btn-primary" onClick={onayla} disabled={uygulaniyor || kalemler.length === 0 || gecersizVar}
                                title={gecersizVar ? 'Oranı 0 ya da boş payı aşan satır var' : undefined}>
                                <i className={`fas ${uygulaniyor ? 'fa-spinner fa-spin' : 'fa-check'}`} style={{ marginRight: 6 }}></i>Onayla ve Ata
                            </button>
                        </div>
                    </div>
                    {asim && <div style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: 6 }}><i className="fas fa-exclamation-triangle" style={{ marginRight: 5 }}></i>Seçim hak edilen payı {tl(-kalanSonra)} aşıyor.</div>}
                </>
            )}
        </div>
    );
};

export default DaireOneriPaneli;
